/**
 * D-147: the agency's subscription in the back office — the banner (trial ending, past due,
 * read-only) shown above every page, and the module flags that hide the screens the plan does
 * not include (the API refuses them too).
 */

import { useSaasStatusRetrieve } from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Button } from "@agencyos/ui-web";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { moduleOf } from "../../lib/settings";
import { dateText } from "../billing/shared";

export const SUBSCRIPTION_PATH = "/settings/subscription";

export function useSaasStatus() {
  const query = useSaasStatusRetrieve({ query: { staleTime: 60_000, retry: false } });
  return query.data?.status === 200 ? query.data.data : undefined;
}

/** The Django apps the plan closes (empty without a subscription). */
export function useDisabledApps(): readonly string[] {
  return useSaasStatus()?.disabled_apps ?? [];
}

/** True when the screen of this route belongs to a module the plan does not include. */
export function isRouteDisabled(pathname: string, disabled: readonly string[]): boolean {
  const app = moduleOf(pathname);
  return app !== "" && disabled.includes(app);
}

export function formatAmount(amount: number): string {
  return formatXaf(amount);
}

/** The banner of the back office: nothing when all is well. */
export function SaasBanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const status = useSaasStatus();
  if (!status?.subscribed) return null;
  const language = i18n.resolvedLanguage ?? "fr";
  const invoice = status.open_invoice as { number?: string; due_at?: string } | null;
  let text = "";
  let tone = "warning";
  if (status.read_only) {
    text = t("saas.banner.readOnly");
    tone = "danger";
  } else if (status.status === "past_due") {
    text = t("saas.banner.pastDue", {
      number: invoice?.number ?? "",
      date: dateText(invoice?.due_at ?? null, language),
      grace: dateText(status.grace_ends_at, language),
    });
  } else if (status.status === "trial" && invoice) {
    text = t("saas.banner.trial", { date: dateText(status.trial_ends_at, language) });
  }
  if (!text) return null;
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={`saas-banner saas-banner--${tone} ao-glass`}>
      <span>{text}</span>
      {invoice ? (
        <Button size="sm" variant={tone === "danger" ? "primary" : "secondary"} onClick={() => navigate(SUBSCRIPTION_PATH)}>
          {t("saas.banner.pay")}
        </Button>
      ) : null}
    </div>
  );
}
